import { useEffect, useMemo, useState } from 'react';
import { FiMap, FiInbox } from 'react-icons/fi';
import { PortalPage } from '../../components/layout/PortalPage';
import { Spinner } from '../../components/ui/Spinner';
import {
  MapaManifestacoesPublico, MODULO_LABEL, MODULO_COR, type PinoPublico,
} from '../../components/MapaManifestacoesPublico';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

const MODULOS = ['ouvidoria', 'esic', 'zeladoria', 'protocolo_cidadao'];

export function MapaPublicoPage() {
  const [pinos, setPinos] = useState<PinoPublico[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [ativos, setAtivos] = useState<Record<string, boolean>>(
    Object.fromEntries(MODULOS.map((m) => [m, true])),
  );

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data } = await supabase.rpc('mapa_publico_manifestacoes', { p_estabelecimento_id: getEstabelecimentoAtualId() });
      if (!vivo) return;
      setPinos((data ?? []) as PinoPublico[]);
      setCarregando(false);
    })();
    return () => { vivo = false; };
  }, []);

  const contagem = useMemo(() => {
    const c: Record<string, number> = {};
    for (const m of MODULOS) c[m] = 0;
    for (const p of pinos) c[p.modulo_codigo] = (c[p.modulo_codigo] ?? 0) + 1;
    return c;
  }, [pinos]);

  const filtrados = useMemo(() => pinos.filter((p) => ativos[p.modulo_codigo]), [pinos, ativos]);

  return (
    <PortalPage
      titulo="Mapa de demandas"
      subtitulo="Todas as manifestações públicas distribuídas no mapa do município."
      icone={<FiMap size={26} />}
      voltarHref="/"
      largura="larga"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {MODULOS.map((m) => {
            const on = ativos[m];
            return (
              <label
                key={m}
                className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                  on ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'
                }`}
              >
                <input type="checkbox" checked={on} onChange={() => setAtivos((p) => ({ ...p, [m]: !p[m] }))} className="h-4 w-4 accent-institutional" />
                <span className="inline-block h-3 w-3 rounded-full" style={{ background: MODULO_COR[m] }} />
                <span className="font-medium text-slate-700">{MODULO_LABEL[m]}</span>
                <span className="tabular-nums text-xs text-slate-400">({contagem[m] ?? 0})</span>
              </label>
            );
          })}
        </div>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : pinos.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-300"><FiInbox size={32} /></div>
            <div className="text-lg font-bold text-slate-700">Nenhuma demanda no mapa ainda</div>
            <p className="mt-1 max-w-md text-sm text-slate-500">
              Aparecem aqui as manifestações públicas com localização informada.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            <MapaManifestacoesPublico pinos={filtrados} />
          </div>
        )}
      </div>
    </PortalPage>
  );
}
